import type { ReactNode } from 'react';
import useTranslation, { type TextKeyCode } from 'canvas_states/translation/useTranslation';
import type Currency from '../../../../types/payment/Currency';
import useAppConfig from 'canvas_states/config/useAppConfig';
import useCurrentLanguage from 'canvas_states/language/useCurrentLanguage';
import formatAmount from './utils/formatAmount';
import formatCurrencyCodeAndSymbol from './utils/formatCurrency';
import type { UpCurrencyConfigEntry } from '../../../../types/Configuration';
import useNativeFormatCurrency from './useNativeFormatCurrency';
import { pointsCurrency } from './getProperCurrencyByPaymentOption';

const useFormatCurrency = () => {
  const { t } = useTranslation().actions;

  const { config } = useAppConfig().states;
  const { currentLanguage } = useCurrentLanguage().states;
  const { formatAmountWithCurrencyForceDigits, formatAmountWithoutCurrency, formatAmountWithCurrency } =
    useNativeFormatCurrency().actions;
  const currencyConfig = config.currencyFormatSettings;

  const actions = {
    /**
     * ⚠️ WARNING: using this may result in app crashes if rendered inside `React.Fragment`s and should only be used when passing result as a `string` -> `t()` such as in:
     * ```
          const { t } = useTranslation().actions;
          t('offer.modify.bid-card-action.boost-your-bid.button', unsafeFormatCurrencyPure(amount, currency));
     ```
    */
    unsafeFormatAmountWithCurrencyPure: (amount: number, currency: Currency) =>
      formatAmountWithCurrency(amount, currency),

    /**
     * wraps currency method with a safety wrapper <span> to inhibit external translations
     * @see StopExtTranslation
     */
    formatAmountWithCurrency: (amount: number, currency: Currency) => (
      <StopExtTranslation>{actions.unsafeFormatAmountWithCurrencyPure(amount, currency)}</StopExtTranslation>
    ),

    /**
     * ⚠️ WARNING: using this may result in app crashes if rendered inside `React.Fragment`s and should only be used when passing result as a `string` -> `t()` such as in:
     * ```
          const { t } = useTranslation().actions;
          t('offer.modify.bid-card-action.boost-your-bid.button', unsafeFormatCurrencyPure(amount, currency));
     ```
    */
    unsafeFormatAmountWithoutCurrencyPure: (amount: number, currency: Currency) => {
      const roundedAmount = formatAmountWithoutCurrency(amount, currency);
      return formatAmount(roundedAmount, currencyConfig as UpCurrencyConfigEntry);
    },

    /**
     * wraps amount method with a safety wrapper <span> to inhibit external translations
     * @see StopExtTranslation
     */
    formatPointsAmountWithoutCurrency: (amount: number) => (
      <StopExtTranslation>{actions.unsafeFormatAmountWithoutCurrencyPure(amount, pointsCurrency)}</StopExtTranslation>
    ),

    /**
     * wraps currency method with a safety wrapper <span> to inhibit external translations
     * @see StopExtTranslation
     */

    formatAmountWithCurrencyForceDigits: (amount: number, currency: Currency) => (
      <StopExtTranslation>{formatAmountWithCurrencyForceDigits(amount, currency)}</StopExtTranslation>
    ),
    /**
     * ⚠️ WARNING: using this may result in app crashes if rendered inside `React.Fragment`s and should only be used when passing result as a `string` -> `t()` such as in:
     * ```
          const { t } = useTranslation().actions;
          t('offer.modify.bid-card-action.boost-your-bid.button', unsafeFormatCurrencyPure(amount, currency));
     ```
    */
    unsafeFormatCurrencyPure: (currency: Currency) =>
      formatCurrencyCodeAndSymbol(
        currencyConfig,
        currency.symbol,
        t(currency.label as TextKeyCode),
        currentLanguage.code,
      ),
    /**
     * wraps currency method with a safety wrapper <span> to inhibit external translations
     * @see StopExtTranslation
     */
    formatCurrency: (currency: Currency) => (
      <StopExtTranslation>{actions.unsafeFormatCurrencyPure(currency)}</StopExtTranslation>
    ),
  };

  return {
    actions,
  };
};

/**
 * Prevents `<font>` injection translation coming from external/extensions (namely Google Translate).
 * This happens when external translations mix with `<React.Fragment>`s. The issue happens because Google Translate
 * (or another extension) tries to manipulate the DOM at the same time as React, where it introduces
 * an unexpected hierarchy (`<font>`) than what React expects, manifesting in an error originating from
 * within React itself: `NotFoundError: Failed to execute 'removeChild' on 'Node'`. _We're just caught in the middle._
 *
 * We already handle translations on the partner/app side and explicitly want to
 * promote that instead of external sourced translations.
 *
 * ### Why just here?
 * So far this only appears to be happening when the user is directly interacting with
 * setting/resetting a displayed price (such as in the `<BidSlider>` -> `<UpgradeDescription>`)
 * and does not appear to be affecting our translation engine in `useTranslation()`
 *
 * @see https://github.com/facebook/react/issues/17256 for well documented reproducible cases
 * @see https://bugs.chromium.org/p/chromium/issues/detail?id=872770 for Google's take
 * @see https://github.com/facebook/react/issues/17256#issuecomment-1022045269 for an app-wide suggested fix; or
 * @see https://github.com/Program-AR/pilas-bloques-react/pull/142 to see it applied at a more granular level
 * @see https://github.com/facebook/react/issues/11538#issuecomment-417504600 for a more invasive monkey-patch to React
 */
const StopExtTranslation = ({ children }: { children: ReactNode }) => (
  <span style={{ display: 'contents' }} translate="no">
    {children}
  </span>
);

export default useFormatCurrency;
